import { renderToStaticMarkup } from 'react-dom/server';

import { components } from 'react-select';

import parse from 'html-react-parser';

import { shuffle } from 'lodash';
import { register } from '../../i18n';

import clozeMathService from './ClozeMathService';
import promptService from './PromptService';
import styleService from './StyleService';
import utilsService from './UtilsService';

import CustomHtmlOption from '../components/lessonItems/CustomHtmlOption';
import MultiSelectValueLabel from '../components/lessonItems/MultiSelectValueLabel';

const t = register('GlobalQuestionLabels');

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      {styleService.getSvgCaretDown()}
    </components.DropdownIndicator>
  );
};

const TitledControl = ({ children, ...props }) => {
  const selectedOption = props.getValue()[0];
  const titleText = selectedOption ? utilsService.stripTagsAndEntities(selectedOption.viewLabel) : '';

  return (
    <components.Control {...props}>
      <div className='custom-control' title={titleText}>
        {children}
      </div>
    </components.Control>
  );
};

// react-select anchors a portalled menu to the control's DOM node. Rebuilding
// these components per render remounts that node, and a menu left open across
// the remount (multi-select) re-measures a detached control and lands in the
// top left corner of the screen, so the identities have to stay stable.
const ClearIndicator = () => null;

const MultiSelectValueRemove = (props) => {
  if (props.selectProps?.showAnswerFeedback) {
    return null;
  }
  return <components.MultiValueRemove {...props} />;
};

const DEFAULT_COMPONENTS = {
  Control: components.Control,
  DropdownIndicator,
  Option: CustomHtmlOption
};

const MULTI_SELECT_COMPONENTS = {
  Control: components.Control,
  ClearIndicator,
  DropdownIndicator,
  MultiValueLabel: MultiSelectValueLabel,
  MultiValueRemove: MultiSelectValueRemove,
  Option: CustomHtmlOption
};

const TITLED_CONTROL_COMPONENTS = {
  Control: TitledControl,
  DropdownIndicator,
  Option: CustomHtmlOption
};

export default class DropdownService {
  // eslint-disable-next-line no-empty-pattern
  static getDropdownConfig = ({
    disableControlBorder = false,
    isMultiSelect = false,
    modalLeft,
    modalTop,
    model,
    showAnswerFeedback = false,
    shouldHideControl,
    shouldHideValueContainer,
    useCustomControl = false
  } = {}) => {
    const { getStyleVar } = styleService;
    const customDropdownStyles = {
      control: (provided, state) => {
        shouldHideControl = shouldHideControl || (model && !utilsService.isNullOrEmpty(model.mathText));
        return shouldHideControl ? {
          maxHeight: 0,
          maxWidth: 0,
          [shouldHideValueContainer ? 'div' : '']: {
            div: {
              color: 'transparent',
              marginLeft: '0',
              marginRight: '0'
            }
          }
        } : {
          ...provided,
          'backgroundColor': state.isFocused ? getStyleVar('--theme-dropdown-closed-menu-focus') : 'transparent',
          'border': disableControlBorder ? 'none' : undefined,
          'borderColor': getStyleVar('--theme-dropdown-control-border-color'),
          'boxShadow': getStyleVar('--theme-dropdown-control-box-shadow'),
          'fontSize': getStyleVar('--theme-font-size'),
          'maxWidth': getStyleVar('--theme-cloze-blank-dropdown-max-width'),
          'minHeight': getStyleVar('--theme-dropdown-control-height'),
          'minWidth': getStyleVar('--theme-dropdown-control-min-width'),
          'width': getStyleVar('--theme-dropdown-control-width'),
          [!state.isDisabled ? 'div' : '']: {
            div: {
              color: isMultiSelect
                ? getStyleVar('--theme-dropdown-option-foreground')
                : (state.isFocused ? getStyleVar('--theme-dropdown-open-menu-text') : 'inherit'),
            }
          },
          [state.isDisabled ? 'div' : '']: {
            div: {
              color: 'inherit'
            }
          },
          ':hover': {
            backgroundColor: state.isFocused && state.menuIsOpen ? (
              getStyleVar('--theme-dropdown-closed-menu-focus')
            ) : (isMultiSelect ? getStyleVar('--theme-dropdown-option-hover') : getStyleVar('--theme-dropdown-option-selected')),
            borderColor: getStyleVar('--theme-dropdown-control-border-color-hover'),
            cursor: 'pointer',
            [!state.isDisabled ? 'div' : '']: {
              div: {
                color: isMultiSelect
                  ? getStyleVar('--theme-dropdown-option-foreground')
                  : (!state.menuIsOpen ? getStyleVar('--theme-dropdown-closed-menu-text') : getStyleVar('--theme-dropdown-open-menu-text'))
              }
            }
          }
        };
      },
      dropdownIndicator: (provided, _state) => {
        return model.mathText ? {
          display: 'none'
        } : {
          ...provided,
          'img': {
            margin: getStyleVar('--theme-dropdown-indicator-margin')
          },
          ':hover': {
            /* placeholder to prevent default styles here */
          }
        };
      },
      indicatorContainer: (provided, _state) => {
        return {
          ...provided,
          transition: 'none'
        };
      },
      indicatorSeparator: (_provided, _state) => {
        return {
          display: getStyleVar('--theme-dropdown-separator-display')
        };
      },
      input: (provided, _state) => {
        return {
          ...provided,
          padding: getStyleVar('--theme-dropdown-input-padding')
        };
      },
      menu: (provided, state) => {
        const iconGutter = (isMultiSelect && showAnswerFeedback) ? 48 : (isMultiSelect ? 28 : 0);
        return {
          ...provided,
          cursor: 'pointer',
          maxWidth: isMultiSelect ? 'none' : getStyleVar('--theme-cloze-blank-dropdown-max-width'),
          minWidth: `calc(${getStyleVar('--theme-dropdown-control-min-width') || '120px'} + ${iconGutter}px)`,
          overflowX: isMultiSelect ? 'visible' : 'hidden',
          textOverflow: isMultiSelect ? 'clip' : 'ellipsis',
          width: 'max-content',
          right: this.isMenuOffScreen(state) && 0
        };
      },
      menuList: (provided, _state) => {
        const iconGutter = (isMultiSelect && showAnswerFeedback) ? 48 : (isMultiSelect ? 28 : 0);
        return {
          ...provided,
          maxWidth: isMultiSelect ? 'none' : getStyleVar('--theme-cloze-blank-dropdown-max-width'),
          minWidth: `calc(${getStyleVar('--theme-dropdown-control-min-width') || '120px'} + ${iconGutter}px)`,
          overflowX: isMultiSelect ? 'visible' : 'hidden',
          textOverflow: isMultiSelect ? 'clip' : 'ellipsis',
          width: 'max-content'
        };
      },
      menuPortal: (provided, _state) => {
        // TODO remove // const PORTAL_TOP_OFFSET = -200;
        const PORTAL_LEFT_OFFSET = (model && !utilsService.isNullOrEmpty(model.mathText)) ? -15 : 0;
        const PORTAL_TOP_OFFSET = (model && !utilsService.isNullOrEmpty(model.mathText)) ? 15 : -25;
        return {
          ...provided,
          ...(!model?.body && (model && !utilsService.isNullOrEmpty(model.mathText)) ? {
            left: modalLeft + PORTAL_LEFT_OFFSET,
            top: (modalTop || getStyleVar('--theme-dropdown-math-menu-portal-top')) + PORTAL_TOP_OFFSET,
          } : {}),
          zIndex: 1000
        };
      },
      option: (provided, state) => {
        const selectedBackground = getStyleVar('--theme-dropdown-option-selected');
        const hoverBackground = getStyleVar('--theme-dropdown-option-hover');
        return {
          ...provided,
          'backgroundColor': state.isSelected
            ? selectedBackground
            : ((isMultiSelect ? false : state.isFocused) ? hoverBackground : 'transparent'),
          'color': getStyleVar('--theme-dropdown-option-foreground'),
          'cursor': getStyleVar('--theme-dropdown-option-cursor'),
          ':hover': {
            backgroundColor: state.isSelected ? selectedBackground : hoverBackground
          }
        };
      },
      placeholder: (provided, _state) => {
        const shouldHidePlaceholder = (model && !utilsService.isNullOrEmpty(model.mathText));
        return shouldHidePlaceholder ? {
          display: 'none'
        } : provided;
      },
      singleValue: (provided, _state) => {
        return {
          ...provided,
          div: {
            p: {
              margin: '0 !important'
            }
          }
        };
      },
      valueContainer: (provided, _state) => {
        return {
          ...provided,
          padding: getStyleVar('--theme-dropdown-control-padding'),
          ...(isMultiSelect ? { color: getStyleVar('--theme-dropdown-option-foreground') } : {})
        };
      },
      multiValue: (provided, _state) => {
        return {
          ...provided,
          backgroundColor: '#e6e6e6',
          color: getStyleVar('--theme-dropdown-option-foreground')
        };
      },
      multiValueLabel: (provided, _state) => {
        return {
          ...provided,
          color: getStyleVar('--theme-dropdown-option-foreground')
        };
      }
    };

    return {
      customComponents: useCustomControl
        ? TITLED_CONTROL_COMPONENTS
        : (isMultiSelect ? MULTI_SELECT_COMPONENTS : DEFAULT_COMPONENTS),
      customDropdownStyles
    };
  }

  static stripHtmlFromOptionText = (answerText) => {
    const matches = [...answerText.matchAll(/<p[^>]*>(.*?)<\/p>/gi)];

    const plainTexts = matches.map(m => {
      let html = m[1];

      html = html.replace(/<img[^>]*alt="([^"]+)"[^>]*>/gi, '$1');

      html = html.replace(/<[^>]+>/g, '');

      return html.trim();
    });

    return plainTexts.join(' ');
  }

  static getDropdownOptions = ({
    dataId,
    lessonElementState, // optional
    model,
    prependSelectLabel = false
  } = {}) => {
    const prompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model
    });

    let dropdownOptions = prompt?.answers?.map((answer, index) => {
      const isStringAnswer = typeof answer === 'string';
      let answerText = isStringAnswer ? answer.trim() : answer.text;
      answerText = prompt.answerType !== 'multichoice' ? answerText : renderToStaticMarkup(parse(answerText || '', {
        replace: (domNode) => {
          if (domNode.name === 'math') {
            clozeMathService.appendDataIdToMathDomNodeThenConvertToMencloseIfApplicable(domNode, dataId, model);
          }
        }
      }));
      const { cleanStringForScoring } = utilsService;
      const plainText = this.stripHtmlFromOptionText(answerText);
      const mes = cleanStringForScoring(plainText);
      const answerId = !isStringAnswer && answer?.id ? answer.id : null;

      return {
        dataId,
        label: (model?.responseFormat === 'html') ?  mes : answerText,
        viewLabel: answerText,
        originalData: isStringAnswer
          ? { text: answer, dataId }
          : { ...answer, dataId },
        // Never invent a GUID here: students don't receive validation, so a generated
        // value would be submitted and fail scoring. Fall back to the choice label.
        value: answerId || answerText || `option-${dataId}-${index}`
      };
    }) || [];
    dropdownOptions = model?.isRandomized ? shuffle(dropdownOptions) : dropdownOptions;
    dropdownOptions = prependSelectLabel || (model && !utilsService.isNullOrEmpty(model.mathText)) ? [
      {
        dataId: 'prependedSelectLabel',
        label: `${t('choose')}...`,
        value: 'prependedSelectLabel'
      },
      ...dropdownOptions
    ] : dropdownOptions;
    return dropdownOptions;
  }

  static isMenuOffScreen = (state) => {
    const { innerRef } = state;
    if (innerRef?.current) {
      const { right } = innerRef.current.getBoundingClientRect();
      if (right > window.innerWidth) {
        return true;
      }
    }
    return false;
  };
}
